import React, { ReactNode } from "react";
import styled from "styled-components";
import { Theme } from "@literati/public-ui-shared-react";
import {
  Content,
  DialogContentProps,
  DialogOverlayProps,
  DialogPortalProps,
  DialogProps,
  Overlay,
  Portal,
  Root,
  Trigger,
} from "@radix-ui/react-dialog";
import { onboardingBookShuffleDialogPadding } from "components/Onboarding/onboardingBookShuffleStyles";
import { fadeOutOpacity } from "utils/animations/keyframes";
import {
  bookShuffleCardSharedStyles,
  dialogOverlayStyles,
  fadeInOverlayRgba,
} from "components/bookShuffle/bookShuffleStyles";

/** This is the className that will be pre-pended to all of BookCheckoutCardDialog's inner component classNames */
export const BOOK_SHUFFLE_CARD_DIALOG_CLASS_BASE = "book-shuffle-card-dialog";

/** These are all of the internal classNames that are used within the BookCheckoutCardDialog component */
export const BookShuffleCardDialogClassName = {
  BASE: BOOK_SHUFFLE_CARD_DIALOG_CLASS_BASE,
  OVERLAY: `${BOOK_SHUFFLE_CARD_DIALOG_CLASS_BASE}-overlay`,
  CONTENT: `${BOOK_SHUFFLE_CARD_DIALOG_CLASS_BASE}-content`,
  CARD: `${BOOK_SHUFFLE_CARD_DIALOG_CLASS_BASE}-card`,
} as const;

const StyledOverlay = styled(Overlay)`
  ${dialogOverlayStyles}
  ${onboardingBookShuffleDialogPadding}

  &[data-state='open'] {
    animation: ${fadeInOverlayRgba} 500ms;
  }

  &[data-state='closed'],
  .${BookShuffleCardDialogClassName.CONTENT}[data-state='closed'] {
    animation: ${fadeOutOpacity} 500ms;
  }
`;

const Card = styled.div`
  ${bookShuffleCardSharedStyles}
  flex-direction: column;
  padding: 2rem 1.25rem 2.5rem;
  height: 22rem;
  width: 21rem;
  max-width: 100vw;
`;

export type BookShuffleCardDialogOptions = {
  contentProps?: DialogContentProps;
  portalProps?: DialogPortalProps;
  overlayProps?: DialogOverlayProps;
  rootProps?: DialogProps;
};

export interface BookShuffleCardDialogProps {
  children: ReactNode;
  className?: string;
  dialogOptions?: BookShuffleCardDialogOptions;
  open: boolean;
  trigger?: ReactNode;
}

export const BookShuffleCardDialog = ({
  children,
  className,
  dialogOptions,
  open,
  trigger,
}: BookShuffleCardDialogProps) => {
  const cls = BookShuffleCardDialogClassName;

  return (
    <Theme>
      <Root open={open} {...dialogOptions?.rootProps}>
        {trigger && <Trigger asChild>{trigger}</Trigger>}
        <Portal {...dialogOptions?.portalProps}>
          <StyledOverlay
            {...dialogOptions?.overlayProps}
            className={`${cls.BASE} ${cls.OVERLAY} ${className}`}
          >
            <Content {...dialogOptions?.contentProps} className={cls.CONTENT}>
              <Card className={cls.CARD}>{children}</Card>
            </Content>
          </StyledOverlay>
        </Portal>
      </Root>
    </Theme>
  );
};

export default BookShuffleCardDialog;
